import _ from "lodash";

/**
 * Keeps the Generate TTS Audio voice/engine choice valid for Amazon Polly and turns
 * TTS failures into messages that say what actually went wrong.
 *
 * Polly rejects a request whose voice does not support the selected engine
 * ("This voice does not support the selected engine: standard"). The dialog remembers
 * the voice and the engine separately in local storage, so a neural-only voice combined
 * with the default Standard engine used to fail every preview with a generic error.
 */

export const TTS_ENGINE_PREFERENCE = ["neural", "standard", "generative", "long-form"];

const ENGINE_LABELS = {
	standard: "Standard",
	neural: "Neural",
	generative: "Generative",
	"long-form": "Long-Form"
};

export function normalizeTtsEngine(engine) {
	return _.isString(engine) ? engine.trim().toLowerCase() : "";
}

export function formatTtsEngine(engine) {
	const normalized = normalizeTtsEngine(engine);
	return ENGINE_LABELS[normalized] || (normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : "");
}

export function getTtsVoiceEngines(voice) {
	if (!_.isPlainObject(voice)) {
		return [];
	}
	let engines = _.isArray(voice.SupportedEngines) ? voice.SupportedEngines : [];
	if (engines.length === 0 && _.isString(voice.SupportedEnginesList)) {
		engines = voice.SupportedEnginesList.split(",");
	}
	return _.uniq(engines.map(normalizeTtsEngine).filter(Boolean));
}

export function ttsVoiceSupportsEngine(voice, engine) {
	const normalized = normalizeTtsEngine(engine);
	return !!normalized && getTtsVoiceEngines(voice).includes(normalized);
}

export function describeTtsVoice(voice) {
	if (!_.isPlainObject(voice)) {
		return "The selected voice";
	}
	const name = voice.Name || voice.Id || "The selected voice";
	return voice.LanguageName ? `${name} (${voice.LanguageName})` : name;
}

export function pickTtsEngineForVoice(voice, preferredEngine) {
	const engines = getTtsVoiceEngines(voice);
	const preferred = normalizeTtsEngine(preferredEngine);
	if (engines.length === 0) {
		return preferred || TTS_ENGINE_PREFERENCE[1];
	}
	if (engines.includes(preferred)) {
		return preferred;
	}
	return TTS_ENGINE_PREFERENCE.find(engine => engines.includes(engine)) || engines[0];
}

function sortedVoices(voices) {
	return _.sortBy(_.filter(voices, _.isPlainObject), ["LanguageName", "Name"]);
}

export function pickTtsVoiceForEngine(voices, engine, currentVoice) {
	const candidates = sortedVoices(voices).filter(voice => ttsVoiceSupportsEngine(voice, engine));
	if (candidates.length === 0) {
		return null;
	}
	if (_.isPlainObject(currentVoice)) {
		const sameLanguageAndGender = candidates.find(voice => voice.LanguageCode === currentVoice.LanguageCode && voice.Gender === currentVoice.Gender);
		if (sameLanguageAndGender) {
			return sameLanguageAndGender;
		}
		const sameLanguage = candidates.find(voice => voice.LanguageCode === currentVoice.LanguageCode);
		if (sameLanguage) {
			return sameLanguage;
		}
	}
	return candidates[0];
}

/**
 * Returns a voice/engine pair Polly accepts.
 *
 * `changed` says what the user just edited: "engine" keeps the engine and moves to a
 * compatible voice (same language and gender when possible); anything else keeps the
 * voice and moves to an engine it supports (neural first, then standard).
 */
export function reconcileTtsVoiceAndEngine({ voiceId, engine, changed } = {}, voices = []) {
	const list = sortedVoices(voices);
	const requestedEngine = normalizeTtsEngine(engine);
	let voice = _.find(list, { Id: voiceId }) || null;
	const reasons = [];

	if (!voice) {
		voice = (requestedEngine && pickTtsVoiceForEngine(list, requestedEngine, null)) || list[0] || null;
		if (voice) {
			reasons.push(`Voice "${voiceId || ""}" is not available; using ${describeTtsVoice(voice)}.`);
		}
	}

	if (!voice) {
		return { voiceId: voiceId || "", engine: requestedEngine || "standard", changed: false, reasons };
	}

	let resolvedEngine = requestedEngine;
	if (!ttsVoiceSupportsEngine(voice, resolvedEngine)) {
		if (changed === "engine" && requestedEngine) {
			const replacement = pickTtsVoiceForEngine(list, requestedEngine, voice);
			if (replacement) {
				reasons.push(`${describeTtsVoice(voice)} does not support the ${formatTtsEngine(requestedEngine)} engine; switched to ${describeTtsVoice(replacement)}.`);
				voice = replacement;
			}
		}
		if (!ttsVoiceSupportsEngine(voice, resolvedEngine)) {
			const previous = resolvedEngine;
			resolvedEngine = pickTtsEngineForVoice(voice, resolvedEngine);
			reasons.push(previous
				? `${describeTtsVoice(voice)} does not support the ${formatTtsEngine(previous)} engine; switched to ${formatTtsEngine(resolvedEngine)}.`
				: `Using the ${formatTtsEngine(resolvedEngine)} engine for ${describeTtsVoice(voice)}.`);
		}
	}

	return {
		voiceId: voice.Id,
		engine: resolvedEngine,
		changed: voice.Id !== voiceId || resolvedEngine !== requestedEngine,
		reasons
	};
}

export function describeTtsVoiceEngineMismatch(voice, engine) {
	const supported = getTtsVoiceEngines(voice).map(formatTtsEngine).join(", ");
	const supportedText = supported ? ` It supports: ${supported}.` : "";
	return `${describeTtsVoice(voice)} does not support the ${formatTtsEngine(engine) || "selected"} engine.${supportedText} Choose another engine or voice and try again.`;
}

export function createTtsFailureError(message, cause) {
	const error = new Error(message);
	error.ttsFailure = true;
	if (cause) {
		error.cause = cause;
	}
	return error;
}

export function assertTtsVoiceEngineCompatible(voice, engine) {
	if (_.isPlainObject(voice) && getTtsVoiceEngines(voice).length > 0 && !ttsVoiceSupportsEngine(voice, engine)) {
		throw createTtsFailureError(describeTtsVoiceEngineMismatch(voice, engine));
	}
}

function collectErrorDetails(value, details, depth = 0) {
	if (!value || typeof value !== "object" || depth > 5) {
		return;
	}
	for (const key of ["code", "name", "message", "jse_shortmsg", "statusCode", "status"]) {
		if (details[key] === undefined && (_.isString(value[key]) || _.isNumber(value[key])) && value[key] !== "") {
			details[key] = value[key];
		}
	}
	for (const key of ["error", "jse_cause", "cause", "body", "response", "originalError"]) {
		if (value[key] && typeof value[key] === "object") {
			collectErrorDetails(value[key], details, depth + 1);
		}
	}
}

export function extractTtsErrorDetails(err) {
	const details = {};
	if (err && typeof err === "object") {
		const status = err.status || _.get(err, "response.status") || _.get(err, "response.statusCode");
		if (_.isNumber(status)) {
			details.httpStatus = status;
		}
		collectErrorDetails(_.get(err, "response.body"), details);
		collectErrorDetails(err, details);
	}
	return details;
}

/**
 * Builds the message shown when a preview or generation request fails.
 * `params` is the request configuration ({ voice, engine }) when known.
 */
export function describeTtsFailure(err, params = {}, fallback = "TTS audio generation failed.") {
	if (err && err.ttsFailure && err.message) {
		return err.message;
	}

	const details = extractTtsErrorDetails(err);
	const name = details.name && details.name !== "Error" ? details.name : "";
	const code = String(details.code || name || "");
	const httpStatus = details.httpStatus;

	if (httpStatus === 401 || httpStatus === 403 || /NotAuthorized|Unauthorized|TokenExpired/i.test(code)) {
		return "Your session has expired. Please sign in again and retry.";
	}

	const voice = params && params.voice;
	const engine = params && params.engine;
	if (_.isPlainObject(voice) && getTtsVoiceEngines(voice).length > 0 && !ttsVoiceSupportsEngine(voice, engine)) {
		return describeTtsVoiceEngineMismatch(voice, engine);
	}

	if (/TextLengthExceeded/i.test(code)) {
		return "The text is too long for Amazon Polly in a single request. Shorten the text and try again.";
	}

	if (/Validation/i.test(code) || details.statusCode === 400) {
		const engineText = engine ? ` with the ${formatTtsEngine(engine)} engine` : "";
		return `Amazon Polly rejected the request for ${describeTtsVoice(voice)}${engineText}. Check the voice, engine, sample rate and text, then try again.`;
	}

	const detail = details.jse_shortmsg || details.message || "";
	const suffix = code ? ` (${code})` : "";
	return detail && detail !== fallback ? `${fallback.replace(/\.$/, "")}${suffix}: ${detail}` : `${fallback.replace(/\.$/, "")}${suffix}.`;
}
